import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { collection, query, where, getDocs, getDoc, updateDoc, doc } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { Mail, Lock, User, Phone, Ticket, AlertCircle, Loader, CheckCircle } from 'lucide-react'

// Google icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

// Apple icon component
const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
)

function SignUpPage() {
  const [step, setStep] = useState(1) // 1: invitation code, 2: user details
  const [invitationCode, setInvitationCode] = useState('')
  const [invitationData, setInvitationData] = useState(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [appleLoading, setAppleLoading] = useState(false)
  const [error, setError] = useState('')

  const { signUp, signUpWithGoogle, signUpWithApple } = useAuth()
  const navigate = useNavigate()

  // Validate invitation code
  const validateInvitationCode = async (e) => {
    e.preventDefault()
    
    if (!invitationCode.trim()) {
      setError('Please enter an invitation code')
      return
    }
    
    try {
      setError('')
      setLoading(true)
      
      // Query for the invitation code
      const invitationsRef = collection(db, 'invitations')
      const q = query(invitationsRef, where('code', '==', invitationCode.toUpperCase().trim()))
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        setError('Invalid invitation code. Please check and try again.')
        return
      }
      
      const inviteDoc = querySnapshot.docs[0]
      const inviteData = inviteDoc.data()
      
      // Check if invitation is already used
      if (inviteData.isUsed) {
        setError('This invitation code has already been used.')
        return
      }
      
      // (Invitation codes do not expire. Older invitation docs may carry an
      //  `expirationDate` field set by the legacy iOS code path that defaulted
      //  to 6 months out — we intentionally ignore it. Codes are only
      //  invalidated when they're used, regenerated, or admin-vacated.)

      // Fetch neighborhood name
      let neighborhoodName = inviteData.neighborhoodId
      try {
        const neighborhoodDoc = await getDoc(doc(db, "neighborhoods", inviteData.neighborhoodId))
        if (neighborhoodDoc.exists()) {
          neighborhoodName = neighborhoodDoc.data().name || inviteData.neighborhoodId
        }
      } catch (e) {
        console.error("Error fetching neighborhood:", e)
      }
      
      // Valid invitation - proceed to step 2
      setInvitationData({
        id: inviteDoc.id,
        ...inviteData,
        neighborhoodName
      })
      setStep(2)

      
    } catch (err) {
      console.error('Error validating invitation:', err)
      setError('Failed to validate invitation code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle sign up
  const handleSignUp = async (e) => {
    e.preventDefault()
    
    if (!firstName || !lastName || !email || !password) {
      setError('Please fill in all required fields')
      return
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    
    try {
      setError('')
      setLoading(true)
      
      // Create user account. Write firstName/lastName alongside the
      // joined `name` so downstream consumers (Android already does this
      // on edit, AdminRegistration writes all three) don't have to parse.
      await signUp(email, password, invitationCode, {
        name: `${firstName.trim()} ${lastName.trim()}`,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone || null,
        address: invitationData.address,
        neighborhoodId: invitationData.neighborhoodId
      })
      
      // Update address status to active
      console.log('Attempting to update address. invitationData:', JSON.stringify(invitationData))
      console.log('addressId from invitation:', invitationData.addressId)
      console.log('neighborhoodId:', invitationData.neighborhoodId)

      try {
        let addressUpdated = false

        // Method 1: Use addressId directly from invitation (most reliable for new invitations)
        if (invitationData.addressId) {
          try {
            const addressRef = doc(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses', invitationData.addressId)
            await updateDoc(addressRef, {
              status: 'active',
              residentUserId: email.toLowerCase(),
              residentName: `${firstName.trim()} ${lastName.trim()}`,
              residentEmail: email.toLowerCase()
            })
            console.log('Address updated successfully using addressId:', invitationData.addressId)
            addressUpdated = true
          } catch (directErr) {
            console.warn('Direct addressId update failed:', directErr.message)
          }
        }

        // Method 2: Fallback - query by invitation code
        if (!addressUpdated) {
          console.log('Trying fallback: query by invitation code:', invitationCode)
          const addressesRef = collection(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses')
          const addressQuery = query(addressesRef, where('invitationCode', '==', invitationCode.toUpperCase().trim()))
          const addressSnapshot = await getDocs(addressQuery)
          console.log('Query result - found addresses:', addressSnapshot.size)

          if (!addressSnapshot.empty) {
            const addressDoc = addressSnapshot.docs[0]
            console.log('Found address doc:', addressDoc.id)
            await updateDoc(addressDoc.ref, {
              status: 'active',
              residentUserId: email.toLowerCase(),
              residentName: `${firstName.trim()} ${lastName.trim()}`,
              residentEmail: email.toLowerCase()
            })
            console.log('Address status updated successfully via query')
          } else {
            console.error('No address found with invitation code:', invitationCode)
          }
        }
      } catch (addrErr) {
        console.error('Error updating address status:', addrErr)
      }
      
      // Mark invitation as used
      await updateDoc(doc(db, 'invitations', invitationData.id), {
        isUsed: true,
        usedAt: new Date(),
        usedBy: email.toLowerCase()
      })
      
      navigate('/')
      
    } catch (err) {
      console.error('Sign up error:', err)
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.')
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.')
      } else {
        setError('Failed to create account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Handle Google Sign Up
  const handleGoogleSignUp = async () => {
    try {
      setError('')
      setGoogleLoading(true)

      const { user, isExisting } = await signUpWithGoogle(invitationCode, invitationData)

      // Mark invitation as used (if not an existing user)
      if (!isExisting) {
        // Update address status to active
        console.log('Google SignUp - Attempting to update address. addressId:', invitationData.addressId)

        try {
          let addressUpdated = false

          // Method 1: Use addressId directly from invitation
          if (invitationData.addressId) {
            try {
              const addressRef = doc(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses', invitationData.addressId)
              await updateDoc(addressRef, {
                status: 'active',
                residentUserId: user.email.toLowerCase(),
                residentName: user.displayName || user.email.split('@')[0],
                residentEmail: user.email.toLowerCase()
              })
              console.log('Address updated successfully using addressId')
              addressUpdated = true
            } catch (directErr) {
              console.warn('Direct addressId update failed:', directErr.message)
            }
          }

          // Method 2: Fallback - query by invitation code
          if (!addressUpdated) {
            const addressesRef = collection(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses')
            const addressQuery = query(addressesRef, where('invitationCode', '==', invitationCode.toUpperCase().trim()))
            const addressSnapshot = await getDocs(addressQuery)

            if (!addressSnapshot.empty) {
              const addressDoc = addressSnapshot.docs[0]
              await updateDoc(addressDoc.ref, {
                status: 'active',
                residentUserId: user.email.toLowerCase(),
                residentName: user.displayName || user.email.split('@')[0],
                residentEmail: user.email.toLowerCase()
              })
              console.log('Address status updated via query')
            } else {
              console.error('No address found with invitation code:', invitationCode)
            }
          }
        } catch (addrErr) {
          console.error('Error updating address status:', addrErr)
        }

        // Mark invitation as used
        await updateDoc(doc(db, 'invitations', invitationData.id), {
          isUsed: true,
          usedAt: new Date(),
          usedBy: user.email.toLowerCase()
        })
      }

      navigate('/')
    } catch (err) {
      console.error('Google sign up error:', err)
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.')
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method.')
      } else {
        setError(err.message || 'Failed to sign up with Google. Please try again.')
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  // Handle Apple Sign Up
  const handleAppleSignUp = async () => {
    try {
      setError('')
      setAppleLoading(true)

      const { user, isExisting } = await signUpWithApple(invitationCode, invitationData)

      // Mark invitation as used (if not an existing user)
      if (!isExisting) {
        // Update address status to active
        console.log('Apple SignUp - Attempting to update address. addressId:', invitationData.addressId)

        try {
          let addressUpdated = false

          // Method 1: Use addressId directly from invitation
          if (invitationData.addressId) {
            try {
              const addressRef = doc(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses', invitationData.addressId)
              await updateDoc(addressRef, {
                status: 'active',
                residentUserId: (user.email || '').toLowerCase(),
                residentName: user.displayName || (user.email ? user.email.split('@')[0] : 'Apple User'),
                residentEmail: (user.email || '').toLowerCase()
              })
              console.log('Address updated successfully using addressId')
              addressUpdated = true
            } catch (directErr) {
              console.warn('Direct addressId update failed:', directErr.message)
            }
          }

          // Method 2: Fallback - query by invitation code
          if (!addressUpdated) {
            const addressesRef = collection(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses')
            const addressQuery = query(addressesRef, where('invitationCode', '==', invitationCode.toUpperCase().trim()))
            const addressSnapshot = await getDocs(addressQuery)

            if (!addressSnapshot.empty) {
              const addressDoc = addressSnapshot.docs[0]
              await updateDoc(addressDoc.ref, {
                status: 'active',
                residentUserId: (user.email || '').toLowerCase(),
                residentName: user.displayName || (user.email ? user.email.split('@')[0] : 'Apple User'),
                residentEmail: (user.email || '').toLowerCase()
              })
              console.log('Address status updated via query')
            } else {
              console.error('No address found with invitation code:', invitationCode)
            }
          }
        } catch (addrErr) {
          console.error('Error updating address status:', addrErr)
        }

        // Mark invitation as used
        await updateDoc(doc(db, 'invitations', invitationData.id), {
          isUsed: true,
          usedAt: new Date(),
          usedBy: (user.email || '').toLowerCase()
        })
      }

      navigate('/')
    } catch (err) {
      console.error('Apple sign up error:', err)
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.')
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method.')
      } else {
        setError(err.message || 'Failed to sign up with Apple. Please try again.')
      }
    } finally {
      setAppleLoading(false)
    }
  }

  // Format phone number as user types
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
    setPhone(value)
  }

  const formatPhoneDisplay = (value) => {
    if (!value) return ''
    const digits = value.replace(/\D/g, '')
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  return (
    <div className="auth-form">
      {step === 1 ? (
        // Step 1: Invitation Code
        <form onSubmit={validateInvitationCode}>
          <div className="auth-form-header">
            <h2>Join NeighborTopia</h2>
            <p>Enter your invitation code to get started</p>
          </div>

          {error && (
            <div className="form-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            
            <div className="input-with-icon">
              <Ticket size={18} className="icon" />
              <input
                id="invitationCode"
                type="text"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                placeholder="ENTER INVITATION CODE"
                style={{ textTransform: 'uppercase' }}
                required
              />
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader size={18} className="spinner" />
                <span>Validating...</span>
              </>
            ) : (
              <span>Register</span>
            )}
          </button>

          <div className="auth-links">
            <p>
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
            <p className="no-invite-text">
              Don't have an invite? Ask your HOA or neighborhood association to{' '}
              <a
                href="https://buy.stripe.com/6oU5kD9I9eDM6gO2kB4Rq03"
                target="_blank"
                rel="noopener noreferrer"
              >
                sign up
              </a>{' '}
              on NeighborTopia!
            </p>
          </div>
        </form>
      ) : (
        // Step 2: User Details
        <form onSubmit={handleSignUp}>
          <div className="auth-form-header">
            <h2>Create Your Account</h2>
            <p>Complete your profile to join {invitationData?.neighborhoodName || invitationData?.neighborhoodId}</p>
          </div>

          {/* Invitation Info Card */}
          <div className="invitation-info">
            <CheckCircle size={20} />
            <div>
              <strong>Invitation Verified</strong>
              <span>{invitationData?.address}</span>
            </div>
          </div>

          {error && (
            <div className="form-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Google Sign Up Button */}
          <button
            type="button"
            className="btn-google"
            onClick={handleGoogleSignUp}
            disabled={googleLoading || appleLoading || loading}
          >
            {googleLoading ? (
              <>
                <Loader size={18} className="spinner" />
                <span>Signing up...</span>
              </>
            ) : (
              <>
                <GoogleIcon />
                <span>Continue with Google</span>
              </>
            )}
          </button>

          {/* Apple Sign Up Button */}
          <button
            type="button"
            className="btn-apple"
            onClick={handleAppleSignUp}
            disabled={appleLoading || googleLoading || loading}
          >
            {appleLoading ? (
              <>
                <Loader size={18} className="spinner" />
                <span>Signing up...</span>
              </>
            ) : (
              <>
                <AppleIcon />
                <span>Continue with Apple</span>
              </>
            )}
          </button>

          <div className="auth-divider">
            <span>or continue with email</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <div className="input-with-icon">
                <User size={18} className="icon" />
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <div className="input-with-icon">
              <Mail size={18} className="icon" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <div className="input-with-icon">
              <Phone size={18} className="icon" />
              <input
                id="phone"
                type="tel"
                value={formatPhoneDisplay(phone)}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <div className="input-with-icon">
              <Lock size={18} className="icon" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <div className="input-with-icon">
              <Lock size={18} className="icon" />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                autoComplete="new-password"
                required
              />
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader size={18} className="spinner" />
                <span>Creating Account...</span>
              </>
            ) : (
              <span>Create Account</span>
            )}
          </button>

          <div className="auth-links">
            <button 
              type="button" 
              onClick={() => { setStep(1); setError(''); }}
              className="link-button"
            >
              ← Use a different invitation code
            </button>
          </div>
        </form>
      )}

      <style>{`
        .invitation-info {
          display: flex;
          align-items: flex-start;
          gap: var(--spacing-md);
          padding: var(--spacing-md);
          background: rgba(124, 182, 95, 0.1);
          border: 1px solid rgba(124, 182, 95, 0.3);
          border-radius: var(--radius-md);
          margin-bottom: var(--spacing-md);
        }
        
        .invitation-info svg {
          color: var(--color-primary);
          flex-shrink: 0;
          margin-top: 2px;
        }
        
        .invitation-info div {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .invitation-info strong {
          color: var(--color-primary-dark);
          font-size: 0.875rem;
        }
        
        .invitation-info span {
          color: var(--color-text-secondary);
          font-size: 0.875rem;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--spacing-md);
        }
        
        .link-button {
          background: none;
          border: none;
          color: var(--color-primary);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
        }
        
        .link-button:hover {
          text-decoration: underline;
        }

        .btn-google {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: white;
          border: 1px solid #dadce0;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          color: #3c4043;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-google:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #c6c6c6;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .btn-google:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-apple {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: #000000;
          border: 1px solid #000000;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 10px;
        }

        .btn-apple:hover:not(:disabled) {
          background: #1a1a1a;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        .btn-apple:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .auth-divider {
          display: flex;
          align-items: center;
          text-align: center;
          margin: 20px 0;
        }

        .auth-divider::before,
        .auth-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid #e0e0e0;
        }

        .auth-divider span {
          padding: 0 16px;
          font-size: 0.8125rem;
          color: #6b7280;
          white-space: nowrap;
        }

        .no-invite-text {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-top: 8px;
        }

        @media (max-width: 480px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

export default SignUpPage
